import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useRecordContext, type RaRecord } from "react-admin";
import {
  buildRazaRequestProgressChecklist,
  type RazaProgressEventInput,
  type RazaProgressStep,
} from "./raza-request-progress-checklist";

type RazaProgressRecord = RaRecord & {
  razaGranted?: string | Date | null;
  lagatReceiptCount?: number | null;
  booking?: { hallBookingCount?: number | null } | null;
  razaEvents?: RazaProgressEventInput[] | null;
};

function StepIcon({ status }: { status: RazaProgressStep["status"] }) {
  if (status === "done") {
    return <CheckCircleIcon fontSize="small" color="success" aria-hidden />;
  }
  if (status === "na") {
    return <RemoveCircleOutlineIcon fontSize="small" color="disabled" aria-hidden />;
  }
  return <RadioButtonUncheckedIcon fontSize="small" color="warning" aria-hidden />;
}

function statusLabel(step: RazaProgressStep): string {
  if (step.detail) return step.detail;
  if (step.status === "done") return "Done";
  if (step.status === "na") return "Not applicable";
  return "To do";
}

/** Compact ops checklist on raza request show (always visible above tabs). */
const RazaRequestProgressChecklist = () => {
  const record = useRecordContext<RazaProgressRecord>();
  if (!record) return null;

  const checklist = buildRazaRequestProgressChecklist({
    razaGranted: record.razaGranted,
    lagatReceiptCount: record.lagatReceiptCount,
    booking: record.booking,
    razaEvents: record.razaEvents,
  });

  const summary =
    checklist.blockingTodoCount === 0
      ? checklist.completedCount === checklist.applicableCount
        ? "All steps complete"
        : `Required steps done · ${checklist.applicableCount - checklist.completedCount} optional open`
      : `${checklist.blockingTodoCount} required step${
          checklist.blockingTodoCount === 1 ? "" : "s"
        } remaining`;

  return (
    <Box
      sx={{
        mt: 2,
        pt: 1.5,
        borderTop: 1,
        borderColor: "divider",
      }}
      role="group"
      aria-label="Raza request progress"
    >
      <Stack
        direction="row"
        alignItems="baseline"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
        columnGap={1}
        rowGap={0.5}
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          Progress
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {checklist.completedCount}/{checklist.applicableCount} · {summary}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={0}
        alignItems="stretch"
        sx={{
          overflowX: "auto",
          pb: 0.5,
          gap: 0.5,
        }}
      >
        {checklist.steps.map((step, index) => {
          const showDetail = Boolean(step.detail);
          return (
            <Box
              key={step.id}
              sx={{
                display: "flex",
                alignItems: "stretch",
                flexShrink: 0,
                minWidth: 0,
              }}
            >
              {index > 0 ? (
                <Box
                  aria-hidden
                  sx={(theme) => ({
                    width: theme.spacing(1.5),
                    alignSelf: "center",
                    height: 0,
                    borderTop: 1,
                    borderColor: "divider",
                    mx: 0.25,
                    flexShrink: 0,
                  })}
                />
              ) : null}
              <Box
                title={statusLabel(step)}
                sx={(theme) => {
                  const titleRow = theme.spacing(2.5);
                  return {
                    display: "grid",
                    gridTemplateColumns: `${titleRow} 1fr`,
                    columnGap: theme.spacing(0.5),
                    rowGap: theme.spacing(0.25),
                    alignContent: "start",
                    px: 0.75,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    width: theme.spacing(17.5),
                    // Title row + 2-line detail + padding
                    minHeight: theme.spacing(7.5),
                    height: "100%",
                    boxSizing: "border-box",
                    "& .RazaProgressStep-icon": {
                      width: titleRow,
                      height: titleRow,
                    },
                    "& .RazaProgressStep-title": {
                      lineHeight: titleRow,
                      height: titleRow,
                    },
                  };
                }}
              >
                <Box
                  className="RazaProgressStep-icon"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <StepIcon status={step.status} />
                </Box>
                <Typography
                  className="RazaProgressStep-title"
                  variant="caption"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: step.status === "na" ? "text.disabled" : "text.primary",
                  }}
                >
                  {step.label}
                  {step.optional ? (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 400, ml: 0.5 }}
                    >
                      (opt)
                    </Typography>
                  ) : null}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  aria-hidden={!showDetail}
                  sx={{
                    gridColumn: 2,
                    lineHeight: 1.2,
                    height: "2.4em",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    visibility: showDetail ? "visible" : "hidden",
                  }}
                >
                  {showDetail ? step.detail : "\u00a0"}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

export default RazaRequestProgressChecklist;
